/**
 * Helpers for the Batting Rules editor. The document is edited as JSON text;
 * the three top-level switches are also shown as toggles that rewrite the text.
 */

export const BATTING_RULES_SWITCHES = [
  {
    key: 'enabled',
    label: 'GDD batting rules',
    help: 'OFF switches the GDD batting layer off for every client (a rollback).',
  },
  {
    key: 'zoneTimingWindows',
    label: 'Legacy zone timing windows',
    help: 'ON brings back the legacy per-zone timing window.',
  },
  {
    key: 'legacyAiColdDowngrade',
    label: 'Legacy AI cold downgrade',
    help: 'ON brings back the legacy AI cold downgrade.',
  },
] as const;

export type BattingRulesSwitch = (typeof BATTING_RULES_SWITCHES)[number]['key'];

export type ParsedBattingRules =
  | { ok: true; doc: Record<string, unknown> }
  | { ok: false; error: string };

/** Parse the editor text. The document must be a JSON object. */
export function parseBattingRules(text: string): ParsedBattingRules {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (error) {
    return { ok: false, error: `Not valid JSON: ${(error as Error).message}` };
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, error: 'The document must be a JSON object.' };
  }
  return { ok: true, doc: value as Record<string, unknown> };
}

/** Pretty-printed text for the editor. */
export function formatBattingRules(doc: Record<string, unknown>): string {
  return JSON.stringify(doc, null, 2);
}

/**
 * Set one top-level switch and keep everything else. Returns the new text, or
 * null when the current text doesn't parse.
 */
export function setBattingRulesSwitch(
  text: string,
  key: BattingRulesSwitch,
  value: boolean
): string | null {
  const parsed = parseBattingRules(text);
  if (!parsed.ok) return null;
  return formatBattingRules({ ...parsed.doc, [key]: value });
}

/** The backend reports every validation problem in one 400, joined by '; '. */
export function splitServerErrors(message: string): string[] {
  return message
    .split('; ')
    .map((part) => part.trim())
    .filter(Boolean);
}
